import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {useNotify, useTranslate} from 'react-admin'
import {DropzoneRootProps, useDropzone} from 'react-dropzone'

import {arrayBufferToBase64, arrayBufferToHex} from '../utils/buffers'
import {COMBINED_ATTACHMENTS_MAX_SIZE} from '../utils/consts'
import {getMaxUploadFileSizeFromMimeType} from '../utils/files'

const useFileSelection: UseFileSelection = ({
  canDragAndDrop = true,
  encoding,
  mimeTypes,
  multiple,
  onSelectFiles,
  shouldCheckForWhatsappBusinessApiMaxFileSize = false,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [files, setFiles] = useState<FileAttachment<typeof encoding>[]>([])
  const remountFileInput = useCallback(() => {
    // We unmount then remount the input component to allow to select the same
    // file multiple times.
    setIsFileInputMounted(false)
    setTimeout(() => setIsFileInputMounted(true), 0)
  }, [])
  const selectFiles = useCallback(async files => {
    const addedFiles = await Promise.all([...files].map(async f => ({
      encodedContent: encoding ?
        {
          'base64': arrayBufferToBase64,
          'hex': arrayBufferToHex,
        }[encoding](await f.arrayBuffer()) :
        undefined,
      file: f,
      filename: f.name,
      mimeType: f.type,
    } as FileAttachment<typeof encoding>)))
    remountFileInput()
    setFiles(previousFiles => [...(multiple ? previousFiles : []), ...addedFiles])
  }, [encoding, multiple, remountFileInput])
  const validator = useCallback<FileSelectionValidator>((file: File) => {
    const hasBackendCompression = encoding === undefined
    const maxFileSize = getMaxUploadFileSizeFromMimeType(file.type, hasBackendCompression)
    const combinedSize = files.reduce((size, f) => size + f.file.size, 0) + file.size
    if (combinedSize > COMBINED_ATTACHMENTS_MAX_SIZE) {
      return {
        code: 'combined-size-too-large',
        message: translate(
          'validations.files.maxCombinedSize',
          {
            combinedSize: (combinedSize / 1_048_576).toFixed(2),
            maxCombinedSize: COMBINED_ATTACHMENTS_MAX_SIZE / 1_048_576,
          },
        ),
      }
    }
    if (
      shouldCheckForWhatsappBusinessApiMaxFileSize &&
      (file.size > maxFileSize)
    ) {
      return {
        code: 'file-too-large',
        message: translate(
          'validations.files.maxSize',
          {
            fileSize: (file.size / 1_048_576).toFixed(2),
            filename: file.name,
            maxSize: maxFileSize / 1_048_576,
          },
        ),
      }
    }
    if (!mimeTypes.includes(file.type)) {
      return {
        code: 'file-invalid-type',
        message: translate(
          'validations.files.unsupportedFileType',
          {filename: file.name, type: file.type},
        ),
      }
    }
    return null
  }, [
    encoding,
    files,
    mimeTypes,
    translate,
    shouldCheckForWhatsappBusinessApiMaxFileSize,
  ])
  const {
    getInputProps,
    getRootProps,
    inputRef: fileInputRef,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: Object.fromEntries(mimeTypes.map(m => [m, []])) as Record<string, string[]>,
    multiple,
    noClick: true,
    noKeyboard: true,
    onDrop: selectFiles,
    onDropRejected: fileRejections => {
      switch (fileRejections[0].errors[0].code) {
        case 'combined-size-too-large':
        case 'file-invalid-type':
        case 'file-too-large':
          notify(fileRejections[0].errors[0].message, {type: 'error'})
          break
        default:
          notify('validations.files.unexpectedError', {type: 'error'})
      }
    },
    validator,
  })
  const [isFileInputMounted, setIsFileInputMounted] = useState(true)
  const input = useMemo<JSX.IntrinsicElements['input'] | null>(
    () => isFileInputMounted ? <input {...getInputProps()} /> : null,
    [getInputProps, isFileInputMounted]
  )
  useEffect(() => {
    files.length && onSelectFiles?.(files)
  }, [files, onSelectFiles])
  return {
    dropZoneProps: canDragAndDrop ? getRootProps() : {},
    files,
    input,
    isDraggedFileRejected: isDragReject,
    isDragging: isDragActive,
    openFileSelectionDialog:
      useCallback(() => fileInputRef.current?.click?.(), [fileInputRef]),
    setFiles,
    validator,
  }
}

interface UseFileSelection {
  <TEncoding extends FileEncoding | undefined>(props: UseFileSelectionProps<TEncoding>): {
    dropZoneProps: DropzoneRootProps
    files: FileAttachment<TEncoding>[]
    input: JSX.IntrinsicElements['input'] | null
    isDraggedFileRejected: boolean
    isDragging: boolean
    openFileSelectionDialog: () => void
    setFiles: Dispatch<SetStateAction<FileAttachment<TEncoding>[]>>
    validator: FileSelectionValidator
  }
}

interface UseFileSelectionProps<TEncoding extends FileEncoding | undefined> {
  canDragAndDrop?: boolean
  encoding?: TEncoding
  mimeTypes: string[]
  multiple?: boolean
  onSelectFiles?: (files: FileAttachment<TEncoding>[]) => void
  shouldCheckForWhatsappBusinessApiMaxFileSize?: boolean
}

export type FileSelectionValidator =
  (file: File) => ({code: string, message: string} | null)

export default useFileSelection
